/**
 * The editor side of the transform gizmo (architecture §6.3).
 *
 * The backend owns the handles and reports drags as world-space poses. This
 * controller decides where the gizmo sits (on the selected entity, in the
 * current tool's mode) and turns each drag into `entity.setTransform` commands
 * that share one interaction session, so a whole drag is one undo step (AC 4).
 *
 * With several entities selected, the gizmo sits at the center of their
 * bounds, turned like the active entity (Unity's Center pivot), and the drag's
 * change to the gizmo applies to each of them: they move, turn and scale about
 * that center as one rigid group. Each frame is then one transaction, and the
 * session's transactions coalesce into one undo step. A selected entity whose
 * ancestor is also selected just follows it.
 *
 * Undo or redo mid-drag aborts the session. The controller then cancels the
 * backend's drag and ignores its remaining events (A-46), so a late pointer
 * move can never write into the history entry that was just undone.
 */
import type { InteractionSession } from "../../commands/CommandBus";
import type { CommandRequest, CommandResult } from "../../commands/types";
import type { Unsubscribe } from "../../core/Emitter";
import { aabbCenter, aabbFromPoints, aabbUnion } from "../../core/math/aabb";
import type { Aabb } from "../../core/math/aabb";
import {
  mat4Compose,
  mat4Decompose,
  mat4GetTranslation,
  mat4Invert,
  mat4Multiply,
} from "../../core/math/mat4";
import type { Mat4 } from "../../core/math/mat4";
import type { Vec3 } from "../../core/math/vec3";
import type { ReadableStore } from "../../core/store";
import type { GizmoDesc, GizmoEvent, GizmoSnap } from "../../render/proxies";
import type { RenderBackend } from "../../render/RenderBackend";
import type { EntityId } from "../../world/entity";
import { topmostOf } from "../../world/hierarchy";
import { localTransformAt } from "../../world/localTransform";
import type { TransformSystem } from "../../world/TransformSystem";
import type { WorldReader } from "../../world/World";
import type { SelectionState } from "../selection/Selection";
import type { SettingsValues } from "../settings/EditorSettings";

export interface GizmoControllerOptions {
  readonly backend: RenderBackend;
  readonly bus: {
    execute(request: CommandRequest): CommandResult;
    transaction<T>(label: string, fn: () => T): T;
    openSession(name: string, onAbort: () => void): InteractionSession;
  };
  readonly world: WorldReader;
  readonly transforms: TransformSystem;
  readonly selection: ReadableStore<SelectionState>;
  readonly settings: ReadableStore<SettingsValues>;
  /** World bounds of what these entities and their children draw (the RenderBridge's). */
  readonly boundsOf: (ids: readonly EntityId[]) => Aabb | null;
  /** The camera the viewport is piloting (moved by navigating, so it gets no gizmo); none by default. */
  readonly piloting?: ReadableStore<EntityId | null>;
}

/** Where the gizmo sits, and the entities a drag of it moves. */
interface Placement {
  readonly pose: Mat4;
  readonly targets: readonly EntityId[];
}

/** One drag in progress. */
interface Drag {
  /** The gizmo's world pose when the drag began. */
  readonly start: Mat4;
  readonly targets: readonly DragTarget[];
  readonly session: InteractionSession;
  /** Commands of one drag coalesce by this id. */
  readonly sessionId: string;
}

/** One entity a drag moves. */
interface DragTarget {
  readonly entity: EntityId;
  /** Its world pose when the drag began. */
  readonly start: Mat4;
  /** The last written rotation, so Euler angles stay continuous (R-5). */
  euler: Vec3;
}

/** Settings store the grid snap in centimeters (Unreal units); the gizmo works in meters. */
const CM_PER_METER = 100;

export class GizmoController {
  private readonly options: GizmoControllerOptions;
  private readonly unsubscribe: Unsubscribe;
  private drag: Drag | null = null;
  private dragCount = 0;
  private shown: GizmoDesc | null = null;

  constructor(options: GizmoControllerOptions) {
    this.options = options;
    this.unsubscribe = options.backend.onGizmo((event) => {
      this.onGizmo(event);
    });
  }

  /** The gizmo's last placed world pose, or null while it's hidden. */
  get pose(): Mat4 | null {
    return this.shown?.world ?? null;
  }

  /** Pushes the gizmo's placement to the backend. Runs in the frame loop's "bridge" phase. */
  flush(): void {
    // Mid-drag the backend already has the gizmo under the pointer.
    if (this.drag) return;

    const next = this.describe();
    if (sameGizmo(this.shown, next)) return;

    this.shown = next;
    this.options.backend.setGizmo(next);
  }

  dispose(): void {
    this.unsubscribe();
    this.drag?.session.close();
    this.drag = null;
    this.options.backend.setGizmo(null);
  }

  // ─── Placement ─────────────────────────────────────────────────────────

  private describe(): GizmoDesc | null {
    const { tool, gameView } = this.options.settings.getSnapshot();
    const placement = this.placement();
    if (tool === "select" || gameView || !placement) return null;

    return { world: placement.pose, mode: tool, snap: this.snap() };
  }

  /**
   * One entity: its own pose, so the gizmo sits on its pivot. Several: the
   * center of their bounds (taking in pivots, for entities that draw nothing),
   * turned like the active entity, at unit scale.
   */
  private placement(): Placement | null {
    const { ids, active } = this.options.selection.getSnapshot();
    if (!active) return null;
    const targets = this.dragTargets(ids, active);
    const { transforms, boundsOf } = this.options;

    const [only] = targets;
    if (targets.length === 1 && only) return { pose: transforms.worldMatrix(only), targets };
    if (targets.length === 0) return null;

    const pivots = aabbFromPoints(
      targets.map((id) => mat4GetTranslation(transforms.worldMatrix(id))),
    );
    const drawn = boundsOf(targets);
    const center = aabbCenter(drawn ? aabbUnion(drawn, pivots) : pivots);
    const turn = mat4Decompose(transforms.worldMatrix(active)).rotation;
    return { pose: mat4Compose(center, turn, [1, 1, 1]), targets };
  }

  private snap(): GizmoSnap | null {
    const settings = this.options.settings.getSnapshot();
    if (!settings.snapEnabled) return null;
    return {
      translate: settings.snapTranslate / CM_PER_METER,
      rotate: settings.snapRotate,
      scale: settings.snapScale,
    };
  }

  // ─── Drags ─────────────────────────────────────────────────────────────

  private onGizmo(event: GizmoEvent): void {
    switch (event.phase) {
      case "start":
        this.beginDrag();
        return;
      case "change":
        this.applyDrag(event.world);
        return;
      case "end":
        this.drag?.session.close();
        this.drag = null;
        return;
    }
  }

  private beginDrag(): void {
    const placement = this.placement();
    if (!placement) return;

    const session = this.options.bus.openSession("gizmo", () => {
      this.drag = null;
      this.options.backend.cancelGizmoDrag();
    });
    const { world, transforms } = this.options;
    this.drag = {
      start: placement.pose,
      targets: placement.targets.map((entity) => ({
        entity,
        start: transforms.worldMatrix(entity),
        euler: world.get(entity)?.components.get("Transform")?.rotation as Vec3,
      })),
      session,
      sessionId: `gizmo:${++this.dragCount}`,
    };
  }

  /**
   * The selected entities a drag moves: those with a Transform, minus any
   * whose ancestor moves, and minus a piloted camera (it's the eye).
   */
  private dragTargets(ids: readonly EntityId[], active: EntityId): EntityId[] {
    const { world, piloting } = this.options;
    const piloted = piloting?.getSnapshot() ?? null;
    return topmostOf(world.getSnapshot().entities, [...ids, active]).filter(
      (id) => id !== piloted && world.get(id)?.components.has("Transform"),
    );
  }

  private applyDrag(world: Mat4): void {
    const drag = this.drag;
    if (!drag?.session.active) return;

    const { bus } = this.options;
    const [only] = drag.targets;
    if (drag.targets.length === 1 && only) {
      // The gizmo sits on the entity's own pose, so the entity takes the gizmo's.
      bus.execute(this.transformRequest(only, world, drag.sessionId));
      return;
    }

    const poseOf = followerPoses(drag.start, world);
    const requests = drag.targets.map((target) =>
      this.transformRequest(target, poseOf(target.start), drag.sessionId),
    );
    bus.transaction(`Transform ${requests.length} entities`, () => {
      for (const request of requests) bus.execute(request);
    });
  }

  /** `entity.setTransform` placing a target at a world pose (Transforms are parent-relative). */
  private transformRequest(target: DragTarget, world: Mat4, session: string): CommandRequest {
    const parent = this.options.world.get(target.entity)?.parent;
    const parentWorld = parent ? this.options.transforms.worldMatrix(parent) : null;
    const local = localTransformAt(world, parentWorld, target.euler);
    target.euler = local.rotation;

    return {
      type: "entity.setTransform",
      payload: { entity: target.entity, ...local, session },
      origin: "gizmo",
    };
  }
}

/**
 * Where the gizmo's move from `from` to `to` takes another pose: the same
 * change, about the gizmo. A zero-scaled gizmo pose has no inverse, so there's
 * no change to carry over and the others stay put.
 */
function followerPoses(from: Mat4, to: Mat4): (start: Mat4) => Mat4 {
  const inverse = mat4Invert(from);
  if (!inverse) return (start) => start;
  const delta = mat4Multiply(to, inverse);
  return (start) => mat4Multiply(delta, start);
}

function sameGizmo(a: GizmoDesc | null, b: GizmoDesc | null): boolean {
  if (a === null || b === null) return a === b;
  return (
    a.mode === b.mode &&
    sameSnap(a.snap, b.snap) &&
    a.world.every((value, i) => value === b.world[i])
  );
}

function sameSnap(a: GizmoSnap | null, b: GizmoSnap | null): boolean {
  if (a === null || b === null) return a === b;
  return a.translate === b.translate && a.rotate === b.rotate && a.scale === b.scale;
}
